import { addDays, addHours, getVisibleHoursBoundaries, isShiftWithinBusinessDay } from '../utils';
import extendCalendarModel from '../model-extensions/extendCalendarModel';
import { WEEKDAYS } from '../constants';

export function splitUnavailability(visibleStart, visibleEnd, item) {
  const itemTail = { ...item };
  itemTail.dtstart = addDays(visibleStart, 1);
  item.dtend = visibleEnd;

  return {
    itemHead: item,
    itemTail,
  };
}

export function splitUnavailabilities(availabilities) {
  return availabilities.reduce((result, item) => {
    if (!item.id) {
      return [...result, item];
    }

    const original = result.find((availability) => availability.id === item.id);
    if (!original) {
      return [...result, item];
    }

    const wasSplit =
      original.dtend.getTime() !== item.dtstart.getTime() &&
      original.dtstart.getTime() !== item.dtend.getTime();
    if (original.fullDay || item.fullDay || wasSplit) {
      return [
        ...result,
        {
          dtstart: item.dtstart,
          dtend: item.dtend,
          fullDay: item.fullDay,
        },
      ];
    }

    return [...result, item];
  }, []);
}

export function flattenUnavailabilites(unavailabilities, setStartDate) {
  const copy = { ...unavailabilities };
  let flattened = Object.keys(copy)
    .map((key) => copy[key])
    .reduce((seed, item) => seed.concat(item), []);

  if (setStartDate) {
    flattened = normalizeDatesToSetStart(flattened, setStartDate);
  }

  flattened = flattened.sort((a, b) => a.dtstart - b.dtstart);
  for (let i = flattened.length - 1; i > 0; i--) {
    if (
      !flattened[i].fullDay &&
      !flattened[i - 1].fullDay &&
      flattened[i].dtstart.getTime() === flattened[i - 1].dtend.getTime()
    ) {
      flattened[i - 1].dtend = flattened[i].dtend;
      flattened.splice(i, 1);
    }
  }

  return flattened;
}

export function normalizeDatesToSetStart(availabilitiesArray, setStartDate) {
  return availabilitiesArray.map((eventArg) => {
    const event = { ...eventArg };
    const sameWeekDay = getFirstWeekdayAfterDate(setStartDate, event.dtstart.getDay());
    sameWeekDay.setHours(event.dtstart.getHours(), event.dtstart.getMinutes(), 0, 0);
    const hoursDiff = Math.round((sameWeekDay.getTime() - event.dtstart.getTime()) / 1000 / 3600);

    event.dtstart = addHours(event.dtstart, hoursDiff);
    event.dtend = addHours(event.dtend, hoursDiff);
    return event;
  });
}

export function getFirstWeekdayAfterDate(dateArg, weekday) {
  let date = dateArg;

  if (![0, 1, 2, 3, 4, 5, 6].includes(weekday)) {
    throw new Error('Invalid weekday provided to _getFirstWeekdayAfterDate');
  }

  while (date.getDay() !== weekday) {
    date = addDays(date, 1);
  }

  return date;
}

export function getAvailabilityArray(data, unavailabilitiesKey = 'availabilities') {
  return data[unavailabilitiesKey].map((availability) => {
    availability.user = data.user;
    return availability;
  });
}

export function getUnavailabilitiesByDay(availabilities, params = {}) {
  const { visibleHoursStart, visibleHoursEnd, extendModel, dayNamesAsKeys } = params;
  let _availabilities = [...availabilities];
  const result = {};

  const processItem = (item) => {
    let dtstart = item.dtstart;
    if (item.fullDay) {
      pushToWeekday(result, dtstart, item);
      return;
    }

    const { visibleStart, visibleEnd } = getVisibleHoursBoundaries(
      visibleHoursStart,
      visibleHoursEnd,
      item.dtstart
    );
    dtstart = visibleStart;
    const isWithinNextDay = isShiftWithinBusinessDay(
      visibleHoursStart,
      visibleHoursEnd,
      addDays(dtstart, 1),
      item
    );
    if (!isWithinNextDay) {
      pushToWeekday(result, dtstart, item);
      return;
    }

    const { itemHead, itemTail } = splitUnavailability(visibleStart, visibleEnd, item);
    pushToWeekday(result, dtstart, itemHead);

    /**
     * itemHead is guaranteed to be not longer than a day but itemTail can be longer
     * let's recursively processs itemTail by the same function
     */
    processItem(itemTail);
  };

  if (extendModel) {
    _availabilities = _availabilities.map(extendCalendarModel);
  }

  _availabilities.forEach(processItem);

  if (dayNamesAsKeys) {
    return mapWeekToDayKeys(result);
  }

  return result;
}

function pushToWeekday(target, dtstart, item) {
  const _date = new Date(dtstart);
  const key = String(_date.getDay());

  if (!(key in target)) {
    target[key] = [];
  }

  target[key].push(item);
}

export function mapWeekToDayKeys(week) {
  const result = {};
  const _weekdays = [...WEEKDAYS];

  // Sling WEEKDAYS start from Monday, js from Sunday
  _weekdays.unshift(_weekdays.pop());

  Object.keys(week).forEach((weekDay) => {
    const key = _weekdays[Number(weekDay)];
    result[key] = week[weekDay];
  });
  return result;
}
